import { useCallback } from 'react'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import { makeStyles, useTheme } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { IRouterLink } from 'core/plugins/model'
import Theme from 'core/themes/model'
import React, { MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import generateTestId from 'utils/test-helpers'
import Text, { TextVariant } from '../Text'
import Tooltip from '../tooltip'
import { createUrlWithQueryString } from 'core/plugins/route'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { KubernetesLogo } from 'k8s/logo'
// usePluginRouter removed - currentPluginId passed as prop to avoid re-renders
import { AppPlugins } from 'app/constants'
import { appStatus } from 'app/plugins/openstack/components/orchestration/app-catalog/helper'
import { topRight } from '../menu/defaults'
import Badge from '../badge'
import ItemHangerIcon from './ItemHangerIcon'

interface Props extends Omit<IRouterLink, 'nestedLinks'> {
  topLevel?: boolean
  isActive?: boolean
  tooltip?: string
  drawerOpen?: boolean
  compact?: boolean
  activeDisplayType?: 'background' | 'bar'
  className?: string
  tooltipProps?: { [key: string]: any }
  variant?: TextVariant
  onClick?: (event: MouseEvent) => void
  disabled?: boolean
  isBetaVersion?: boolean
  highlight?: boolean
  isLastChild?: boolean
  isLeaf?: boolean
  betaVersionTooltip?: string
  currentPluginId?: string
}

const LOGOS = { kubernetes: KubernetesLogo }

export default function NavItem({
  name,
  link,
  icon,
  onClick = undefined,
  className = undefined,
  drawerOpen = false,
  isActive = false,
  compact = false,
  tooltip = '',
  tooltipProps = {},
  activeDisplayType = 'background',
  variant = 'subtitle2',
  disabled = false,
  isBetaVersion = false,
  betaVersionTooltip = '',
  topLevel = false,
  highlight = false,
  isLastChild = false,
  isLeaf = false,
  currentPluginId = '',
}: Props) {
  const isHeader = !!icon
  // currentPluginId now passed as prop from parent
  const isAccountPlugin = currentPluginId === AppPlugins.MyAccount
  const theme = useTheme<Theme>()
  const classes = useStyles()

  const handleClick = useCallback(
    (e) => {
      if (disabled) {
        return e.preventDefault()
      }

      onClick && onClick(e)
    },
    [onClick, disabled],
  )

  // Replace the link paramteres with values
  const { prefs: k8sGlobalPrefs } = useScopedPreferences('k8sPluginGlobalParams')
  const linkTo = link?.path?.includes(':')
    ? createUrlWithQueryString(link?.path || '', {
        cluster: k8sGlobalPrefs?.cluster as string,
      })
    : link?.path

  // Check if the icon is a logo
  const Logo = LOGOS[icon]
  const showLogo = Logo || icon

  return (
    <Link to={disabled ? '' : linkTo} onClick={handleClick}>
      <li
        className={clsx(classes.navItem, className, {
          [classes.navItemDisabled]: disabled,
          [classes.navItemCompact]: compact,
          [classes.navItemLeaf]: isLeaf,
          [classes.navItemActiveCollapsed]: isActive && !drawerOpen,
          [classes.navItemBeforeActive]: isActive,
          [classes.navItemBeforeHighlight]: highlight,
          [classes.navItemAfterLastChild]: isLastChild,
          [classes.navItemAfterHighlight]: highlight,
        })}
        data-active={isActive ? 'true' : undefined}
      >
        {!topLevel && (
          <ItemHangerIcon
            className={clsx(classes.navItemHanger, { [classes.navItemHangerActive]: isActive })}
            fill={isActive ? theme.components.sidebar.activeIcon : theme.components.sidebar.border}
          />
        )}
        <Tooltip message={tooltip ? tooltip : ''} {...tooltipProps}>
          {showLogo && (
            <div
              className={clsx(classes.navIcon, { [classes.navIconActive]: isActive && drawerOpen })}
            >
              {Logo ? (
                <Logo />
              ) : icon ? (
                <FontAwesomeIcon
                  data-testid={generateTestId(name)}
                  className="nav-icon"
                  title={name}
                  size="lg"
                >
                  {icon}
                </FontAwesomeIcon>
              ) : null}
            </div>
          )}
          {drawerOpen && (
            <div className={classes.badgeContainer}>
              <Text
                className={clsx('nav-text', classes.navText, {
                  [classes.navTextActive]: isActive && !isLeaf,
                  [classes.navTextLeaf]: isLeaf && !isActive,
                  [classes.navTextLeafActive]: isLeaf && isActive,
                })}
                data-testid={generateTestId(name)}
                variant={compact ? 'sidenav2' : variant}
              >
                {name}
              </Text>
              {isBetaVersion && (
                <Tooltip className={classes.betaTagTooltip} message={betaVersionTooltip}>
                  <Badge className={classes.badge} variant={appStatus.deploying} text="Beta" />
                </Tooltip>
              )}
            </div>
          )}
        </Tooltip>
        {isLeaf && isActive ? <div className={`${classes.leafNotchMod} ${classes.notch}`} /> : null}
        {!drawerOpen && topLevel && isActive ? <div className={classes.notch} /> : null}
      </li>
    </Link>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  notch: {
    background: theme.components.sidebar.activeIcon,
    width: '3px',
    height: '100%',
    marginLeft: 'auto',
    borderTopLeftRadius: theme.spacing(0.5),
    borderBottomLeftRadius: theme.spacing(0.5),
  },
  leafNotchMod: {
    height: '20px',
    marginTop: '9px',
  },
  navItemHanger: {
    zIndex: 0,
  },
  navItemHangerActive: {
    zIndex: 9,
  },
  navItem: {
    position: 'relative',
    cursor: 'pointer',
    height: 48,
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    '& .nav-text, & .nav-icon': {
      transition: 'color .2s ease',
    },
    '&:hover .nav-text, &:hover ': {
      color: theme.components.sidebar.hoverText,
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      top: 0,
      left: 0,
      width: 1,
      height: '37%',
      zIndex: 9,
      backgroundColor: theme.components.sidebar.border,
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      top: 0,
      left: 0,
      width: 1,
      height: '102%',
      backgroundColor: theme.components.sidebar.border,
    },
  },
  navItemDisabled: {
    cursor: 'not-allowed',
    '&:hover .nav-text, &:hover ': {
      color: theme.palette.grey[500],
    },
  },
  navItemCompact: {
    height: 32,
  },
  navItemLeaf: {
    height: 32,
  },
  navItemActiveCollapsed: {
    backgroundColor: hexToRgbaCss(theme.components.sidebar.activeIcon, 0.1),
  },
  navItemBeforeActive: {
    '&:before': {
      backgroundColor: theme.components.sidebar.activeIcon,
    },
  },
  navItemBeforeHighlight: {
    '&:before': {
      backgroundColor: theme.components.sidebar.activeIcon,
    },
  },
  navItemAfterLastChild: {
    '&:after': {
      width: 0,
    },
  },
  navItemAfterHighlight: {
    '&:after': {
      backgroundColor: theme.components.sidebar.activeIcon,
    },
  },
  navIcon: {
    borderRadius: theme.spacing(0.5),
    backgroundColor: 'transparent',
    minWidth: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.grey[700],
    '& > svg': {
      width: '20px !important',
      height: '20px !important',
    },
    '& > svg path': {
      fill: '',
    },
    '& > i': {
      width: '20px',
      height: '20px',
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.components.sidebar.text,
    },
  },
  navIconActive: {
    backgroundColor: theme.components.sidebar.activeIcon,
    color: theme.components.sidebar.activeText,
    '& > svg': {
      width: '18px !important',
      height: '18px !important',
    },
    '& > svg path': {
      fill: 'white',
    },
    '& > i': {
      width: '18px',
      height: '18px',
      fontSize: '18px',
      color: theme.palette.grey['000'],
    },
  },
  navText: {
    color: theme.palette.grey[700],
  },
  navTextActive: {
    color: theme.components.sidebar.activeText,
  },
  navTextLeaf: {
    color: theme.palette.grey[500],
  },
  navTextLeafActive: {
    color: theme.palette.grey[700],
  },
  badgeContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  betaTagTooltip: {
    padding: 0,
    '& .tooltip-container': {
      padding: 0,
    },
  },
}))
